
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getMetaAdAccounts } from '@/utils/metaApi/accounts';
import { validateToken } from '@/utils/metaApi/auth';
import { useToast } from '@/components/ui/use-toast';
import { ConnectionState, ConnectionStatus, MetaAccount, ConnectionMethod } from '../types';
import { initiateOAuthFlow } from '@/services/metaApi/auth';

export const useMetaConnect = (onSuccess?: (token: string) => void) => {
  const [activeTab, setActiveTab] = useState<ConnectionMethod>(ConnectionMethod.FACEBOOK);
  const [status, setStatus] = useState<ConnectionStatus>({
    state: ConnectionState.INITIAL,
    message: 'Bitte verbinden Sie Ihr Meta-Konto'
  });
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    state: ConnectionState.INITIAL,
    message: 'Bitte verbinden Sie Ihr Meta-Konto'
  });
  const [accounts, setAccounts] = useState<MetaAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [sdkConfigError, setSdkConfigError] = useState<string | null>(null);
  const { toast } = useToast();

  // Handle connection to Meta
  const handleConnect = useCallback(async () => {
    try {
      setConnectionStatus({
        state: ConnectionState.CONNECTING,
        message: 'Verbindung wird hergestellt...'
      });
      
      // Initialize OAuth flow (should open popup)
      initiateOAuthFlow();
      
      // Note: The actual token handling will happen in the callback page (Meta-Auth.tsx)
      // If we had a callback function, we'd call it here
      if (onSuccess) {
        // This is just a placeholder as the real token will be handled in the callback
        console.log("OAuth flow initiated, waiting for callback");
      }
    } catch (error) {
      console.error("Error initiating Meta OAuth flow:", error);
      setConnectionStatus({
        state: ConnectionState.ERROR,
        message: error instanceof Error ? error.message : 'Fehler beim Starten des Meta-Logins'
      });
    }
  }, [onSuccess]);

  // Initialize component
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Fetch accounts when token is valid
  const fetchAccounts = async (accessToken: string) => {
    if (!accessToken) return;
    
    try {
      const accountsData = await getMetaAdAccounts(accessToken);
      
      if (accountsData.length === 0) {
        setStatus({
          state: ConnectionState.WARNING,
          message: 'Keine Werbekonten gefunden. Bitte erstellen Sie ein Werbekonto bei Meta.'
        });
      } else {
        setAccounts(accountsData);
        setStatus({
          state: ConnectionState.SUCCESS,
          message: `${accountsData.length} Werbekonten gefunden`
        });
      }
    } catch (error) {
      console.error('Error fetching Meta ad accounts:', error);
      setStatus({
        state: ConnectionState.ERROR,
        message: error instanceof Error ? error.message : 'Fehler beim Abrufen der Werbekonten'
      });
    }
  };

  // Handle account selection
  const handleSelectAccount = useCallback((accountId: string) => {
    setSelectedAccount(accountId);
  }, []);

  // Save connection to Supabase
  const saveConnection = useCallback(async (customerId: string) => {
    if (!selectedAccount || !customerId) {
      toast({
        title: "Fehler",
        description: "Bitte wählen Sie ein Werbekonto und einen Kunden aus.",
        variant: "destructive",
      });
      return false;
    }
    
    try {
      const selectedAccountData = accounts.find(acc => acc.id === selectedAccount);
      if (!selectedAccountData) {
        throw new Error('Ausgewähltes Konto nicht gefunden');
      }
      
      // Store connection in meta_connections table
      const { error } = await supabase.from('meta_connections')
        .insert({
          customer_id: customerId,
          user_id: customerId, // Duplicate for backward compatibility
          ad_account_id: selectedAccount,
          account_name: selectedAccountData.name,
          access_token: "sample-token-for-testing", // In real app, this would be the actual token
          is_connected: true
        });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Verbindung gespeichert",
        description: "Das Meta-Konto wurde erfolgreich mit dem Kunden verknüpft.",
      });
      
      return true;
    } catch (error) {
      console.error('Error saving Meta connection:', error);
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : 'Fehler beim Speichern der Verbindung',
        variant: "destructive",
      });
      return false;
    }
  }, [selectedAccount, accounts, toast]);

  return {
    activeTab,
    setActiveTab,
    status,
    accounts,
    selectedAccount,
    handleSelectAccount,
    saveConnection,
    connectionStatus, 
    setConnectionStatus,
    isInitialized,
    isLoading,
    sdkError,
    sdkConfigError,
    handleConnect
  };
};
